import React from 'react';
import {Row} from 'react-table';

import {Action, Awaitable, EDragData, Order} from '@ELib/utilities/types';

// code moved from webapp, disable conflicting rules in webapp-lib for now.
/* eslint-disable @typescript-eslint/no-explicit-any */

// Columns

export type ETableColumn<Key extends string, Sort> = {
  /** Text that is shown to the user. */
  label: string | React.ReactElement;
  /** Text that is shown to the user when editing columns. */
  labelDescriptor?: string;
  /** If the tooltipTitle is set, a tooltip will be shown as the label with the tooltipTitle as title and label as child */
  tooltipTitle?: string | React.ReactElement;
  /** Internal sort key that may be sent to the backend. */
  sortName?: Sort;
  /** Internal name that is used to map cells to certain columns. */
  name: Key;
  align?: ETableCellAlignment;
  /** Tells the table if this column is off per default */
  defaultHidden?: boolean;
  /**
   * CSS width string, e.g. `20%` or `100px`.
   * Percentages are usually more suitable than pixel values.
   */
  width?: string;
};

// Rows

export type ETableRowID = string;

export type ETableActiveItem = ETableRowID | ETableActiveChildItem;

export type ETableActiveChildItem = {
  parentId: ETableRowID;
  childId: ETableRowID;
};

export const isActiveRowChildItem = (
  activeItem: ETableActiveItem | undefined
): activeItem is ETableActiveChildItem =>
  activeItem !== undefined && (activeItem as any).childId !== undefined;

export const getActiveItemIdOrParentId = (
  activeItem: ETableActiveItem | undefined
): string | undefined =>
  isActiveRowChildItem(activeItem) ? activeItem.parentId : activeItem;

export type ETableRow<Key extends string = string> = Record<Key, ETableCell> & {
  id: ETableRowID;
  /** Store a reference to the original data item. */
  raw?: any;
  options?: ETableRowOptions<Key>;
};

/** Sub row of a table. It has the exact same columns as the parent row but may have other data. */
export type ETableSubRow<Key extends string> = Exclude<
  ETableRow<Key>,
  'options'
> & {
  options?: Pick<ETableRowOptions<Key>, 'singleActions' | 'multiActions'>;
};

export type ETableAction = Omit<Action<any>, 'inactive' | 'text'> & {
  inactive?: boolean;
  text: string;
};
export type ETableRowActionHandler = (row?: Row<ETableRow>) => ETableAction[];

export type ETableSingleAction = Action<any>;
export type ETableMultiAction = Action<any[]>;

export type StyleType =
  | 'info'
  | 'important'
  | 'warning'
  | 'danger'
  | 'disabled'
  | 'hide-border-bottom'
  | 'header'
  | 'bold';

export type ETableRowOptions<Key extends string> = {
  /** Whether a row can be selected using a checkbox. Defaults to `false`. */
  isSelectable?: boolean;
  /**
   * Callback that returns a (async) subcomponent.
   *
   * - If it returns an array, it will show additional rows with the same columns.
   * - If it returns an element, it will add a single new row with a cell containing
   *   the element that spans all content cells.
   * - If it returns an object, it will expect a sub table that only spans certain
   *   columns. Columns that have a `null` value are merged with previous cells.
   */
  expansion?: (row: ETableRow<Key>) => Awaitable<ETableRowExpansion<Key>>;
  /**
   * Set a special styling on the expansion cell.
   *
   * - `hide-border-bottom` will make the bottom border transparent.
   * - `total` will add a double bottom border (usually used for total cells, hence the name).
   */
  expansionStyleType?: 'hide-border-bottom' | 'total';
  /**
   * List of actions that can be applied to a single row.
   * If `singleActions` is undefined, `multiActions` will be applied to a single row.
   */
  singleActions?: ETableSingleAction[];
  /**
   * Whether sub rows have single actions. Defaults to false
   * TODO LEXIAN-2422: This is just a temporary workaround. Remove after implementation.
   */
  hasSubRowSingleAction?: boolean;
  /**
   * List of actions that can be applied to a list of rows.
   * If there is no `singleActions` prop defined, `multiActions` will also apply to a single row.
   */
  multiActions?: ETableMultiAction[];
  /**
   * Set a special styling on a row.
   *
   * - `info` styles the row content as a pale info text. Row cannot be activated.
   * - `important` styles the text color to red.
   * - `warning` sets the row background to orange.
   * - `danger` sets the row background to red.
   * - `disabled` sets the row background to grey
   * - `hide-border-bottom` makes the bottom border transparent (note: can also be set on a cell level).
   * - `header` styles the row content like the header (small and bold). Also makes the row not hoverable and not activatable.
   */
  styleType?: StyleType;
  /**
   * Restricts the highlighting of table rows
   * - `notActivatable` makes the row not activatable (e.g. on click / with the arrow keys the row does not turn blue).
   * - `noHoverEffect` makes a hover on a row have no effects and therefore the row is also not activatable.
   */
  interactionRestrictionLevel?: 'notActivatable' | 'noHoverEffect';
  /**
   * A list of {@link EDragData} to transfer to the drop element.
   * By declaring a `dragDataList`, the row is set to be `draggable`.
   * @example
   * [{format: 'lexian-document-id', data: `id1`}, {format: 'lexian-document-id', data: `id2`}, {format: 'lexian-document-id', data: `id3`}]
   *
   * If multiple rows a dragged, the data of the same format will be concatenated with a comma separator.
   * @example
   * {format: 'lexian-document-id', data: `id1,id2,id3`}
   */
  dragDataList?: EDragData[];
};

export type ETableRowClickHandler = (
  evt: React.MouseEvent,
  row: Row<ETableRow>,
  childRow?: Row<ETableRow>
) => void;

export type ETableRowDragHandler = ({
  dragEvent,
  activeRow,
}: ETableRowDragParams) => void;

export type ETableRowDragParams = {
  dragEvent: React.DragEvent<HTMLTableRowElement>;
  activeRow?: Row<ETableRow>;
};

export type ETableRowExpansion<Key extends string = string> =
  | ETableSubRow<Key>[]
  | ETableSubRow<Key>
  | React.ReactElement;

// Cells

export type ETableCell = ETableCellValue | ETableCellObject;

export type ETableCellValue =
  | React.ReactElement
  | Date
  | string
  | number
  /** Cells with a `null` value are automatically merged with the previous non-null cell using `colspan`. */
  | null
  // the following types are necessary to support translations
  | object
  | undefined;

export type ETableCellObject = {
  cell: ETableCellValue;
  align?: ETableCellAlignment;
  mode?: ETableCellMode;
  /**
   * Set a special styling on a cell.
   *
   * - `hide-border-bottom` will make the bottom border transparent.
   * - `total` will add a double bottom border (usually used for total cells, hence the name).
   */
  styleType?: 'hide-border-bottom' | 'total';
};

export const isCellObject = (
  cell: ETableCellValue | ETableCellObject
): cell is ETableCellObject =>
  Boolean(cell) && Object.prototype.hasOwnProperty.call(cell, 'cell');

export type ETableCellAlignment = 'left' | 'center' | 'right';

/**
 * Specifies the handling of (long) content in a table cell.
 *
 * - `min` makes the column as narrow as possible while not truncating/breaking any content.
 *   You can still define a `width` that is applied as minimal width. `min` is the recommended
 *   mode for icons. But it should also be used for amounts. That will make sure
 *   that amounts are not truncated but might have a minimal width anyway.
 * - `truncate` (default) will truncate long content and append `...`.
 * - `break` will allow long content to break over multiple lines.
 */
export type ETableCellMode = 'min' | 'truncate' | 'break';

/**
 * Props of the main ETable component
 */
export type ETableProps<Key extends string, Sort extends string> = {
  columns: ETableColumn<Key, Sort>[];
  data: ETableRow<Key>[];

  order?: ETableSort<Sort>;
  onOrderChange?: (order: ETableSort<Sort>) => void;

  /**
   * Set the (initial) active item by ID. The state is handled internally, and
   * you do not necessarily need to pass this property unless you want to
   * change the active item from outside the component.
   */
  activeItem?: ETableActiveItem;
  /**
   * Function to be called when the active item changes. If you pass this function,
   * you must keep the active item state outside the table and update the activeItem
   * when this function is called.
   */
  onActiveItemChange?: (activeItem: ETableActiveItem | undefined) => void;

  /**
   * List of (initially) selected (i.e. checked checkbox) table items. The
   * state is handled internally, and you do not necessarily need to pass this
   * property unless you want to change the selection from outside the
   * component.
   */
  selectedItems?: ETableRowID[];
  /** Function to be called when the selection (i.e. checkbox values) changes. */
  onSelectedItemsChange?: (selectedItems: ETableRowID[]) => void;

  /**
   * List of (initially) expanded table items. The state is handled internally,
   * and you do not necessarily need to pass this property unless you want to
   * change the selection from outside the component.
   */
  expandedItems?: ETableRowID[];
  /** Function to be called when the expansion changes. */
  onExpandedItemsChange?: (expandedItems: ETableRowID[]) => void;

  /**
   * Flag indicating whether there is more data available before the currently
   * loaded data rows.
   */
  hasMoreBefore?: boolean;
  /**
   * Flag indicating whether there is more data available after the currently
   * loaded data rows.
   */
  hasMoreAfter?: boolean;
  /**
   * Will be called when the user scrolls to the very first row and there is more
   * data before that. This can be used to lazily load more data.
   */
  onLoadMoreBefore?: () => Promise<void>;
  /**
   * Will be called when the user scrolls to the very last row and there is more
   * data after that. This can be used to lazily load more data.
   */
  onLoadMoreAfter?: () => Promise<void>;

  /**
   * Placeholder content that will be shown when no data is available.
   * Set it to false if you don't want a placeholder.
   */
  placeholder?: string;

  /**
   * Whether selected row should be scrolled into view or not.
   * Default if not provided: true.
   */
  scrollIntoView?: boolean;

  /**
   * Wehter checkboxes should always be shown
   */
  forceCheckboxes?: boolean;

  /**
   * TestId to be used for component or e2e tests (will be added to the table as attribute "data-testid").
   */
  testId?: string;

  /**
   * Whether column headers should be hidden
   */
  hideHeader?: boolean;

  /**
   * used to persist settings for this table, leaving it out will not persist options, like columns order and visibility
   */
  persistenceId?: string;

  /**
   * Whether a refresh indicator should be shown in the table. This has to be set
   * when there is new data available that can be refreshed by the user.
   */
  refreshIndicator?: {
    show: boolean;
    text?: string;
    onRefresh: () => void;
    onClose: () => void;
  };
};

export type ETableSort<SortKey> = {
  columnName: SortKey;
  order: Order;
}[];
